"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import {
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  CalendarDays,
  ExternalLink,
  Heart,
  MapPin,
  Play,
  Radio,
} from "lucide-react";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";
import { formatDate } from "@/lib/format";
import { enroll, likeModule, saveModule } from "@/lib/lecturehub/module-actions";
import type { EventDetailEntity } from "@/lib/lecturehub/types";
import type { Access } from "./paywall";
import { AccessCta, AccessPrice, ctaClass, ctaStyle } from "./AccessCta";

type Props = {
  lang: Lang;
  moduleId: number;
  event: EventDetailEntity | null | undefined;
  /** `moduleAccess(...)` for this reader (./paywall.ts). */
  access: Access;
  /** Opens without paying (`isFreeToUnlock`) — the "Free" price line. */
  free: boolean;
  isPast: boolean;
  isLive: boolean;
  isAuthed: boolean;
  initialLiked: boolean;
  initialSaved: boolean;
  /** The first recording's player, or null when the event has none. */
  watchHref: string | null;
  /** Show the "Save" action alongside "Favorites". Default true; the event
   *  detail passes false so it shows Favorites only (matches other detail pages). */
  showSave?: boolean;
};

export function EventStartCard({
  lang,
  moduleId,
  event: ev,
  access,
  free,
  isPast,
  isLive,
  isAuthed,
  initialLiked,
  initialSaved,
  watchHref,
  showSave = true,
}: Props) {
  const t = useDict();
  const router = useRouter();
  const [liked, setLiked] = useState(initialLiked);
  const [saved, setSaved] = useState(initialSaved);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function loginUrl(): string {
    return `${langPath(lang, "/login")}?redirect=${encodeURIComponent(window.location.pathname)}`;
  }

  function toggleLike() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !liked;
    setLiked(next);
    startTransition(async () => {
      const r = await likeModule(moduleId, next);
      if (!r.ok) {
        setLiked(!next);
        setError(r.error ?? null);
      }
    });
  }

  function toggleSave() {
    if (!isAuthed) {
      router.push(loginUrl());
      return;
    }
    const next = !saved;
    setSaved(next);
    startTransition(async () => {
      const r = await saveModule(moduleId, next);
      if (!r.ok) {
        setSaved(!next);
        setError(r.error ?? null);
      }
    });
  }

  // A PREMIUM event marked free (kind "start") still needs the free
  // enrolment the backend serves it on; the one tap makes it and goes straight
  // to the first recording — never a separate "register" step. Every other
  // event plays the moment `canView` is true.
  function start() {
    if (!watchHref) return;
    setError(null);
    startTransition(async () => {
      const r = await enroll(moduleId);
      if (r.ok) router.push(watchHref);
      else setError(r.error ?? null);
    });
  }

  const location =
    ev?.city && ev?.country ? `${ev.city}, ${ev.country}` : (ev?.city ?? ev?.country ?? null);

  // Same gate as a video (./AccessCta). An event with no recordings has
  // nothing to open or start, so it offers no button there — its date and
  // place below are all there is. A past date never blocks access.
  const nothingToPlay = !watchHref && (access.kind === "open" || access.kind === "start");
  const cta = nothingToPlay ? null : (
    <AccessCta
      access={access}
      lang={lang}
      moduleId={moduleId}
      open={
        watchHref ? (
          <Link href={watchHref} className={ctaClass} style={ctaStyle}>
            <Play className="size-4" aria-hidden />
            {t.detail.watchRecordings}
            <ArrowRight className="size-3.5" aria-hidden />
          </Link>
        ) : null
      }
      onStart={start}
      startLabel={t.detail.clickToWatch}
      pending={pending}
    />
  );

  return (
    // Sticky lives on the parent column wrapper (EventDetailClient) — see
    // CourseStartCard / MediaSidebar for the same pattern.
    <aside className="event-start">
      <div className="bg-card text-card-foreground rounded-card border border-border/60 p-6 md:p-7 overflow-hidden">
        {/* Price + live badge — prominent, standardised with CourseStartCard. */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <AccessPrice access={access} free={free} />
          </div>
          {isLive && !isPast ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-destructive">
              <Radio className="size-3 animate-pulse" aria-hidden />
              {t.detail.live}
            </span>
          ) : null}
        </div>

        {/* CTA + Like — Like as a compact circle to the right of the primary
            CTA, standardised with CourseStartCard / MediaSidebar. */}
        <div className="mt-4 flex items-center gap-2">
          {cta ? <div className="min-w-0 flex-1">{cta}</div> : null}
          <button
            type="button"
            onClick={toggleLike}
            aria-pressed={liked}
            aria-label={liked ? t.detail.liked : t.detail.like}
            title={liked ? t.detail.liked : t.detail.like}
            className={cn(
              "grid size-12 shrink-0 place-items-center rounded-full border transition-colors",
              liked
                ? "border-accent/30 bg-accent/15 text-accent"
                : "border-border/60 text-foreground/55 hover:bg-foreground/[0.04] hover:text-foreground",
            )}
          >
            <Heart className={cn("size-5", liked ? "fill-current" : "")} aria-hidden />
          </button>
          {showSave ? (
            <button
              type="button"
              onClick={toggleSave}
              aria-pressed={saved}
              aria-label={saved ? t.detail.saved : t.detail.save}
              title={saved ? t.detail.saved : t.detail.save}
              className={cn(
                "grid size-12 shrink-0 place-items-center rounded-full border transition-colors",
                saved
                  ? "border-accent/30 bg-accent/15 text-accent"
                  : "border-border/60 text-foreground/55 hover:bg-foreground/[0.04] hover:text-foreground",
              )}
            >
              {saved ? (
                <BookmarkCheck className="size-5" aria-hidden />
              ) : (
                <Bookmark className="size-5" aria-hidden />
              )}
            </button>
          ) : null}
        </div>

        <dl className="mt-6 grid gap-2.5 text-[12.5px]">
          {ev?.dateFrom ? (
            <Row
              Icon={CalendarDays}
              label={t.detail.starts}
              value={formatDate(ev.dateFrom, lang)}
            />
          ) : null}
          {ev?.dateTo && ev.dateTo !== ev.dateFrom ? (
            <Row Icon={CalendarDays} label={t.detail.ends} value={formatDate(ev.dateTo, lang)} />
          ) : null}
          {location ? <Row Icon={MapPin} label={t.detail.location} value={location} /> : null}
        </dl>

        {ev?.url ? (
          <a
            href={ev.url}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-1.5 text-[11.5px] font-display font-bold uppercase tracking-[0.12em] text-accent hover:brightness-110 transition-all"
          >
            <ExternalLink className="size-3.5" aria-hidden />
            {t.detail.eventWebsite}
          </a>
        ) : null}

        {error ? <p className="text-xs text-destructive mt-3">{error}</p> : null}
      </div>
    </aside>
  );
}

function Row({
  Icon,
  label,
  value,
}: {
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="size-4 text-muted-foreground" aria-hidden />
      <dt className="text-muted-foreground flex-1">{label}</dt>
      <dd className="font-bold text-foreground text-right">{value}</dd>
    </div>
  );
}
